import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { showLoginModal } from "../user-area/utils/loginModal";
import { AuthController } from "../user-area/controllers/auth-controller";
import { EnemExercisesApiService } from "./api";
import { showJotForm, JOTFORM_IDS } from "../utils/showJotForm";
import { ExercisesChatBot } from "./bot/exercises-chat-bot";
import { ExercisesChatBotButton } from "./bot/exercises-chat-bot-shortcut";

@customElement("multiple-choice-question")
export class MultipleChoiceQuestion extends LitElement {
  private authController = new AuthController(this);
  private apiService = new EnemExercisesApiService(this.authController);

  @property({ type: String, attribute: "data-exercise-id" })
  exerciseId = "";

  @property({ type: String, attribute: "data-question-id" })
  questionId = "";

  @property({ type: String, attribute: "data-option-identifier" })
  optionIdentifier = "";

  @property({ type: Boolean, attribute: "data-has-explanation" })
  hasExplanation = false;
  
  @property({ type: String, attribute: "data-title" })
  title = "";

  @property({ type: String, attribute: "data-question" })
  question = "";

  @property({ type: Array, attribute: "data-options" })
  options: string[] = [];

  @state()
  submitted = false;

  @state()
  isCorrect = false;

  @state()
  selectedOption: HTMLInputElement | null = null;

  @state()
  explanation?: string;

  @state()
  explanationLimitReached = false;

  @state()
  userHadTrialBefore = false;

  private correctAnswerId: string | null = null;
  private correctOptionElement: HTMLInputElement | null = null;
  correctOptionLetter: string | null = null;
  private radioInputs: HTMLInputElement[] = [];
  private button: HTMLButtonElement | null = null;
  private answeredStatusElement: HTMLDivElement | null = null;
  private messageElement: HTMLSpanElement | null = null;
  private chatBotButton: ExercisesChatBotButton | null = null;
  private chatBot: ExercisesChatBot | null = null;

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();

    // Parse the encrypted correct answer ID right away
    try {
      // Browser-compatible base64 decoding
      this.correctAnswerId = atob(this.optionIdentifier);
    } catch (e) {
      console.error(`Failed to parse option identifier for ${this.id}:`, e);
    }

    // Check if DOMContentLoaded is already fired
    if (document.readyState === "complete") {
      this.initialize();
    } else {
      window.addEventListener("DOMContentLoaded", this.initialize.bind(this));
    }
  }

  disconnectedCallback() {
    // Clean up event listeners
    this.button?.removeEventListener("click", this.handleAnswer.bind(this));
    this.radioInputs.forEach((radio) => {
      radio.removeEventListener("change", this.handleRadioChange.bind(this));
    });
    window.removeEventListener("DOMContentLoaded", this.initialize.bind(this));

    super.disconnectedCallback();
  }

  initialize() {
    // Get all necessary elements
    this.radioInputs = Array.from(
      this.querySelectorAll<HTMLInputElement>(
        'input[type="radio"][data-option-letter]'
      )
    );
    this.correctOptionElement = this.querySelector(
      `input[value="${this.correctAnswerId}"]`
    );
    this.correctOptionLetter =
      this.correctOptionElement?.getAttribute("data-option-letter") || "";
    this.messageElement = this.querySelector(
      ".enem-exercises-results-list-item-content-actions-answer-message"
    );
    this.button = this.querySelector(
      ".enem-exercises-results-list-item-content-actions-validate"
    );
    this.answeredStatusElement = this.querySelector(
      ".enem-exercises-results-list-item-answered-status"
    );

    this.chatBotButton = this.querySelector(
      "exercises-chat-bot-shortcut"
    ) as ExercisesChatBotButton;

    this.chatBot = document.querySelector("exercises-chat-bot");

    // Set up event listeners
    this.setupRadioListeners();
    this.setupButtonListener();
  }

  setupRadioListeners() {
    if (this.radioInputs.length === 0) return;

    this.radioInputs.forEach((radio) => {
      radio.addEventListener(
        "change",
        (this.handleRadioChange = this.handleRadioChange.bind(this))
      );
    });
  }

  setupButtonListener() {
    if (!this.button) return;

    this.button.toggleAttribute("disabled", true);
    this.button.addEventListener(
      "click",
      (this.handleAnswer = this.handleAnswer.bind(this))
    );
  }

  handleRadioChange(e: Event) {
    const radio = e.target as HTMLInputElement;
    this.selectedOption = radio;

    if (this.button) {
      this.button.removeAttribute("disabled");
    }
  }

  async handleAnswer() {
    if (!this.authController.isLogged) {
      showLoginModal("Faça login para poder responder a questão");
      return;
    }

    if (!this.selectedOption || !this.correctAnswerId) return;

    this.isCorrect = this.selectedOption.value === this.correctAnswerId;

    this.disableInteractions();

    this.toggleAttribute("data-loading", true);
    const response = await this.apiService.answerQuestion({
      exerciseId: this.exerciseId,
      questionId: this.questionId,
      answerId: this.selectedOption.value,
      isCorrect: this.isCorrect,
    });
    this.toggleAttribute("data-loading", false);

    if (!response) {
      // TODO - Show error message
      this.enableInteractions();
      return;
    }

    if (response.showForm) {
      requestAnimationFrame(() => {
        showJotForm(
          JOTFORM_IDS.EXERCISES,
          this.authController.user?.id?.toString() ?? "",
          this.authController.user?.email ?? ""
        );
      });
    }

    // Add data-answered attribute to the component
    this.submitted = true;
    this.explanation = response.explanation;
    this.explanationLimitReached = response.explanation_limit_reached;
    this.userHadTrialBefore = response.user_had_trial_before ?? false;
    this.toggleAttribute("data-answered", true);

    // Mark correct option
    if (this.correctOptionElement?.parentElement) {
      this.correctOptionElement.parentElement.setAttribute("data-correct", "");
    }

    // If the answer is incorrect, mark the selected option
    if (!this.isCorrect && this.selectedOption.parentElement) {
      this.selectedOption.parentElement.setAttribute("data-incorrect", "");
    }

    // Show the appropriate message
    this.updateResultMessages();

    if (!this.explanationLimitReached && this.chatBotButton) {
      import('./bot/exercises-chat-bot');
      this.chatBotButton.showChatButton();
      this.chatBotButton.addEventListener('open', (e: CustomEvent) => this.activateChatBot(e.detail.message));
    }

    this.dispatchEvent(
      new CustomEvent("answer-submitted", {
        detail: {
          isCorrect: this.isCorrect,
          selectedOption: this.selectedOption,
          correctOption: this.correctAnswerId,
          correctOptionLetter: this.correctOptionLetter,
          hasExplanation: this.hasExplanation,
          explanation: response?.explanation,
          explanationLimitReached: response?.explanation_limit_reached,
          userHadTrialBefore: response?.user_had_trial_before,
        },
      })
    );
  }

  activateChatBot(message: string) {
    if (this.chatBot) {
      this.chatBot.openChat(
        message,
        this.questionId,
        this.title,
        this.question,
        this.options,
        this.correctOptionLetter?.trim() || "",
        this.explanation || ""
      );
    }
  }

  updateResultMessages() {
    if (!this.messageElement) return;

    if (this.isCorrect) {
      this.messageElement.toggleAttribute("data-correct", true);
      this.messageElement.innerHTML =
        '<span class="mark-icon"></span><p>Parabéns! Resposta certa.</p>';
    } else {
      this.messageElement.toggleAttribute("data-incorrect", true);
      this.messageElement.innerHTML = `<span class="mark-icon"></span><p>Ops. A alternativa certa é a <strong>${this.correctOptionLetter?.trim()}</strong>.</p>`;
    }

    if (this.answeredStatusElement) {
      this.answeredStatusElement.setAttribute(
        "data-status",
        this.isCorrect ? "correct" : "incorrect"
      );
      const textElement = this.answeredStatusElement.querySelector(".text");
      if (textElement) {
        textElement.innerHTML = this.isCorrect
          ? "Resolvi certo!"
          : "Resolvi errado!";
      }
    }
  }

  disableInteractions() {
    this.button?.toggleAttribute("disabled", true);
    this.radioInputs.forEach((radio) => {
      radio.toggleAttribute("disabled", true);
    });
  }

  enableInteractions() {
    this.button?.toggleAttribute("disabled", false);
    this.radioInputs.forEach((radio) => {
      radio.toggleAttribute("disabled", false);
    });
  }

  render() {
    return html`<slot></slot>`;
  }
}
